import isNil from 'lodash/isNil';
import moment from 'moment-timezone';
import { mergeDeepWithKey, map, pick, pipe } from 'ramda';
import { cloneDeep, get, groupBy, isEmpty, set } from 'lodash';
import {
  prepareSchedule,
  prepareDaysForForm,
  onlyWholeDays,
  generateDaysWithShopTimes,
} from '../../../../../common/helpers/schedules';
import { LOCATIONS_ALL_ID } from '../constants';

import { INDETERMINATE } from '../../../../../common/constants';
import { barberOptionalFields } from '../../../../../common/helpers/modelSettings';
import { prepareModelTimeForForm } from '../../../../../common/helpers/modelTime';
import { getBrandBarberDataByShopId } from '../../../selector';
import { hoursMinutesFields } from './constants';
import { parseCustomerBookingInterval, parseMultiFieldValues } from '../../../../../common/helpers/times';

const startDate = moment.utc().toDate();

export const getInitialValuesFromShop = pipe(pick([
  'bookNoPay',
  'tipEnabled',
  'allowMultipleServices',
  'minServiceLen',
  'bookingInterval',
  'advanceBookDays',
  'advanceBookMins',
  'advanceCancelMins',
  'customerReminder',
  'canCustomerCancel',
]), parseMultiFieldValues);

export const getDefaultBarberLocationInitialValues = ({
  minInterval,
  shopSchedules,
}) => ({
  firstName: '',
  lastName: '',
  color: '#b6dff7',
  userEnabled: true,
  schedules: {
    current: {
      startDate,
      workdays: generateDaysWithShopTimes(false, shopSchedules),
    },
    future: {
      workdays: generateDaysWithShopTimes(false, shopSchedules),
    },
  },
  notifications: {
    confirm: {
      email: true,
      phone: false,
      push: true,
    },
    reschedule: {
      email: false,
      phone: true,
      push: true,
    },
    cancel: {
      email: false,
      phone: true,
      push: true,
    },
    reminder: {
      email: true,
      phone: false,
      push: true,
      time: {
        hours: 0,
        minutes: 45,
      },
    },
    arrived: {
      email: false,
      phone: true,
      push: true,
    },
  },
  permissions: {
    appointments: {
      view: true,
      show_contact_info: true,
      book: true,
      modify: true,
      history: true,
      blockTime: true,
    },
    clients: {
      view: true,
      add: true,
      modify: true,
    },
    services: {
      view: true,
      add: true,
      modify: true,
    },
    workHours: {
      view: true,
      add: true,
      modify: true,
    },
    bankAccount: {
      view: true,
      add: true,
      modify: true,
    },
    profile: {
      view: true,
      modify: true,
    },
    reports: {
      view: true,
    },
    customCharge: {
      view: true,
    },
    autoPayout: {
      view: true,
    },
    engage: {
      view: true,
    },
  },
  advanceBookDays: 60,
  advanceBookMins: {
    hours: 0,
    minutes: 30,
  },
  advanceCancelMins: {
    hours: 1,
    minutes: 0,
  },
  allowMultipleServices: true,
  trackEmployeeTime: false,
  enabled: true,
  minServiceLen: minInterval,
  bookingInterval: minInterval,
});

const showFirstNonEmptyFields = [
  'firstName',
  'lastName',
  'color',
  'username',
  'populateUsername',
  'personnelId',
  'phone',
  'email',
  'avatar',
];

const SERVICE_FORM_FIELDS = ['cost', 'duration', 'assigned'];

const mergeServicesToAll = (allLocationsBarberField, oneLocationBarberField) => {
  if (!allLocationsBarberField) {
    // eslint-disable-next-line no-param-reassign
    allLocationsBarberField = {};
  }

  Object.entries(oneLocationBarberField).forEach(([serviceId, service]) => {
    if (!allLocationsBarberField[serviceId]) {
      // eslint-disable-next-line no-param-reassign
      allLocationsBarberField[serviceId] = {
        ...service,
        shopIds: [LOCATIONS_ALL_ID, service.locationId],
      };
    } else {
      // eslint-disable-next-line no-param-reassign
      allLocationsBarberField[serviceId] = {
        ...allLocationsBarberField[serviceId],
        shopIds: [...allLocationsBarberField[serviceId].shopIds, service.locationId],
      };
    }

    SERVICE_FORM_FIELDS.forEach((fieldName) => {
      if (allLocationsBarberField[serviceId][fieldName] !== service[fieldName]) {
        // eslint-disable-next-line no-param-reassign
        allLocationsBarberField[serviceId][fieldName] = INDETERMINATE;
      }
    });
  });

  return allLocationsBarberField;
};

const mergeBarberFieldsToAll = (key, allLocationsBarberField, oneLocationBarberField) => {
  if (allLocationsBarberField !== oneLocationBarberField) {
    return INDETERMINATE;
  }

  return allLocationsBarberField;
};

const populateWithFirstNonEmptyFields = (allLocationsBarberValues, barberDataByLocationId) => {
  const barberDataByLocationValues = Object.values(barberDataByLocationId);

  showFirstNonEmptyFields.forEach((showFirstNonEmptyField) => {
    const firstNonEmpty = barberDataByLocationValues.find(
      locationBarberData => !!get(locationBarberData, showFirstNonEmptyField)
    );

    if (firstNonEmpty) {
      set(allLocationsBarberValues, showFirstNonEmptyField, get(firstNonEmpty, showFirstNonEmptyField));
    }
  });
};

const populateAllLocationsEngagePermission = (allLocationsBarberValues, barberDataByLocationId) => {
  const barberDataByLocationValues = Object.values(barberDataByLocationId);
  const locationsWithEngagePermissionOnCount = barberDataByLocationValues.filter(
    (locationBarberData) => locationBarberData.permissions.engage.view
  ).length;

  // eslint-disable-next-line max-len
  let allLocationsEngagePermissionValue = locationsWithEngagePermissionOnCount === barberDataByLocationValues.length;
  if (
    locationsWithEngagePermissionOnCount > 0
    && locationsWithEngagePermissionOnCount < barberDataByLocationValues.length
  ) {
    allLocationsEngagePermissionValue = INDETERMINATE;
  }

  // eslint-disable-next-line no-param-reassign
  allLocationsBarberValues.permissions.engage.view = allLocationsEngagePermissionValue;
};

export const getAllLocationsBarberValues = (barberDataByLocationId) => {
  const allLocationsBarberValues = Object.values(barberDataByLocationId).reduce(
    (allLocationsBarber, nextLocationBarber) => {
      if (nextLocationBarber.shopId === LOCATIONS_ALL_ID) {
        return allLocationsBarber;
      }
      const allLocationsServices = allLocationsBarber.services;
      const nextLocationServices = nextLocationBarber.services;

      // eslint-disable-next-line no-param-reassign
      delete allLocationsBarber.services;
      // eslint-disable-next-line no-param-reassign
      delete nextLocationBarber.services;

      const result = mergeDeepWithKey(mergeBarberFieldsToAll, allLocationsBarber, nextLocationBarber);

      // eslint-disable-next-line no-param-reassign
      nextLocationBarber.services = nextLocationServices;

      result.services = mergeServicesToAll(allLocationsServices, nextLocationServices);

      return result;
    },
    {}
  );

  allLocationsBarberValues.shopId = LOCATIONS_ALL_ID;
  allLocationsBarberValues.id = LOCATIONS_ALL_ID;
  allLocationsBarberValues.schedules = {};
  populateWithFirstNonEmptyFields(allLocationsBarberValues, barberDataByLocationId);
  populateAllLocationsEngagePermission(allLocationsBarberValues, barberDataByLocationId);

  return allLocationsBarberValues;
};

const prepareShopDataForEdit = brandShops => (barberShopDataFromApi) => {
  const barberShop = brandShops.find(shop => shop.id === barberShopDataFromApi.shopId);

  const result = barberOptionalFields(cloneDeep(barberShopDataFromApi));

  if (result.photos && result.photos.length) {
    result.avatar = result.photos[0].url;
  }

  result.days_off = prepareDaysForForm(onlyWholeDays(result.days_off));
  result.days_on = prepareDaysForForm(result.days_on);

  result.schedules = prepareSchedule(result.schedules);
  if (!result.schedules.current) {
    result.schedules.current = {
      startDate: moment.utc().startOf('day').toDate(),
      workdays: generateDaysWithShopTimes(false, barberShop.schedules),
    };
  }

  if (!result.schedules.future || !result.schedules.future.startDate) {
    result.schedules.future = {
      workdays: generateDaysWithShopTimes(false, barberShop.schedules),
    };
  }

  result.populateUsername = result.userEnabled && !isEmpty(result.email) && result.email === result.username;

  result.minServiceLen = parseCustomerBookingInterval(result);
  hoursMinutesFields.forEach(field => prepareModelTimeForForm(result, field));

  result.services = (result.services || []).reduce((services, service) => {
    // eslint-disable-next-line no-param-reassign
    services[service.id] = {
      ...service,
      assigned: (result.assignedServices || []).includes(service.id),
    };

    return services;
  }, {});

  delete result.assignedServices;

  result.shopKind = barberShop.kind;

  return result;
};

export const getBarbersDataForEdit = (state, brandShops) => {
  const brandBarberDataByShopId = getBrandBarberDataByShopId(state);
  const servicesDataByShopId = groupBy(Object.values(state.services.byId)
    .map(({ assignments, ...service }) => assignments
      .filter(({ assignmentType, assigneeId }) => (
        assignmentType === 'shop' && !!brandBarberDataByShopId[assigneeId]
      ))
      .map(
        assignment => ({
          ...service,
          locationId: assignment.assigneeId,
          cost: !isNil(assignment.cost) ? assignment.cost : service.cost,
          duration: !isNil(assignment.duration) ? assignment.duration : service.duration,
        })
      ))
    .flat(), 'locationId');

  Object.entries(servicesDataByShopId).forEach(([shopId, services]) => {
    brandBarberDataByShopId[shopId].services = services;
  });

  const barbersDataForEdit = map(prepareShopDataForEdit(brandShops), brandBarberDataByShopId);

  if (Object.keys(barbersDataForEdit).length > 1) {
    barbersDataForEdit[LOCATIONS_ALL_ID] = getAllLocationsBarberValues(barbersDataForEdit);
  }

  return barbersDataForEdit;
};

const mergeServicesFromAll = (allLocationsBarberField, oneLocationBarberField) => Object.entries(
  oneLocationBarberField
).reduce((services, [id, service]) => {
  const allLocationsService = allLocationsBarberField[id];

  SERVICE_FORM_FIELDS.forEach((fieldName) => {
    if (allLocationsService[fieldName] !== INDETERMINATE) {
      // eslint-disable-next-line no-param-reassign
      service[fieldName] = allLocationsService[fieldName];
    }
  });

  // eslint-disable-next-line no-param-reassign
  services[id] = service;

  return services;
}, {});

const mergeBarberFieldsFromAll = (key, allLocationsBarberField, oneLocationBarberField) => {
  if (key === 'shopId' || key === 'id') {
    return oneLocationBarberField;
  }

  if (allLocationsBarberField === INDETERMINATE) {
    return oneLocationBarberField;
  }

  return allLocationsBarberField;
};

// Changes for engage permission checkbox state on 'All locations' tab now apply to all shops
// since commission/rental shop separation has been removed for engage permissions
const applyEngagePermissionAllToSpecificLocation = (allLocationsBarberValues, oneLocationBarberValues) => {
  if (allLocationsBarberValues.permissions.engage.view !== INDETERMINATE) {
    // eslint-disable-next-line no-param-reassign
    oneLocationBarberValues.permissions.engage.view = allLocationsBarberValues.permissions.engage.view;
  }
};

export const applyAllToSpecificLocation = (barberDataByLocationId, locationId) => {
  if (!barberDataByLocationId
    || !barberDataByLocationId[LOCATIONS_ALL_ID]
    || !barberDataByLocationId[locationId]
  ) {
    return barberDataByLocationId;
  }

  const allLocationsBarber = barberDataByLocationId[LOCATIONS_ALL_ID];
  const specificLocationBarber = barberDataByLocationId[locationId];

  const allLocationsServices = allLocationsBarber.services;
  const specificLocationServices = specificLocationBarber.services;

  // eslint-disable-next-line no-param-reassign
  delete allLocationsBarber.services;
  // eslint-disable-next-line no-param-reassign
  delete specificLocationServices.services;

  const specificLocationUpdated = mergeDeepWithKey(
    mergeBarberFieldsFromAll,
    allLocationsBarber,
    specificLocationBarber
  );

  applyEngagePermissionAllToSpecificLocation(allLocationsBarber, specificLocationUpdated);

  specificLocationUpdated.services = mergeServicesFromAll(
    allLocationsServices,
    specificLocationServices
  );

  allLocationsBarber.services = allLocationsServices;

  return specificLocationUpdated;
};

export const fieldNameInFormSection = (
  formSectionName,
  fieldName,
) => `${formSectionName || ''}${formSectionName ? '.' : ''}${fieldName}`;
